import React, { useState, useEffect, useCallback } from 'react';
import { Table, Card, Button, Tag, Space, Modal, Form, Input, Switch, App as AntdApp, Tooltip, Badge, Popconfirm, Row, Col, Statistic } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, ReloadOutlined, CheckCircleOutlined, StopOutlined, KeyOutlined, TeamOutlined, SearchOutlined } from '@ant-design/icons';
import { agentsAPI } from '../services/api';
import socketService from '../services/socket';
import { useNavigate } from 'react-router-dom';
import './Dashboard.css';

const { TextArea } = Input;

const Agents = ({ user }) => {
  const navigate = useNavigate();
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [editingAgent, setEditingAgent] = useState(null);
  const [credentialModalVisible, setCredentialModalVisible] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [agentSearch, setAgentSearch] = useState('');
  const [form] = Form.useForm();
  const { message: antMessage } = AntdApp.useApp();

  // Fetch agents
  const fetchAgents = useCallback(async () => {
    setLoading(true);
    try {
      const response = await agentsAPI.getAll();
      if (response.success) {
        setAgents(response.data.agents || []);
      }
    } catch (error) {
      antMessage.error('Gagal mengambil data agents');
      console.error('Error fetching agents:', error);
    } finally {
      setLoading(false);
    }
  }, [antMessage]);

  useEffect(() => {
    fetchAgents();
  }, [fetchAgents]);

  useEffect(() => {
    const socket = socketService.getSocket();
    if (!socket) return;

    const handleStatusChange = (data) => {
      setAgents(prev => prev.map(agent =>
        agent.agent_id === data.agent_id
          ? { ...agent, online: data.online, last_seen: data.timestamp }
          : agent
      ));
    };

    const handleAgentUpdated = (data) => {
      setAgents(prev => prev.map(agent =>
        agent.id === data.agent.id
          ? { ...agent, ...data.agent }
          : agent
      ));
    };

    socket.on('agent-status-change', handleStatusChange);
    socket.on('agent-updated', handleAgentUpdated);
    return () => {
      socket.off('agent-status-change', handleStatusChange);
      socket.off('agent-updated', handleAgentUpdated);
    };
  }, []);

  // Open modal for creating new agent
  const openCreateModal = () => {
    setModalMode('create');
    setEditingAgent(null);
    form.resetFields();
    setModalVisible(true);
  };

  // Open modal for editing agent
  const openEditModal = (agent) => {
    setModalMode('edit');
    setEditingAgent(agent);
    form.setFieldsValue({
      agent_name: agent.agent_name,
      agent_description: agent.agent_description,
      agent_type: agent.agent_type,
      is_active: agent.is_active
    });
    setModalVisible(true);
  };

  // Open credential modal (fetch fresh token/secret from server)
  const openCredentialModal = async (agent) => {
    try {
      const response = await agentsAPI.getById(agent.id);
      if (response.success) {
        setSelectedAgent(response.data);
        setCredentialModalVisible(true);
      }
    } catch (error) {
      antMessage.error('Gagal memuat kredensial agent');
      console.error('Error loading agent credentials:', error);
    }
  };

  // Handle form submission
  const handleSubmit = async (values) => {
    try {
      if (modalMode === 'create') {
        const response = await agentsAPI.create(values);
        if (response.success) {
          antMessage.success('Agent berhasil dibuat — salin kredensial ke agent');
          setModalVisible(false);
          const creds = response.data?.credentials;
          if (creds) {
            setSelectedAgent({
              agent_id: creds.agent_id,
              token: creds.token,
              secret: creds.secret,
            });
            setCredentialModalVisible(true);
          }
          fetchAgents();
        }
      } else {
        const response = await agentsAPI.update(editingAgent.id, values);
        if (response.success) {
          antMessage.success('Agent berhasil diperbarui');
          setModalVisible(false);
          fetchAgents();
        }
      }
    } catch (error) {
      antMessage.error('Gagal menyimpan agent');
      console.error('Error saving agent:', error);
    }
  };

  // Handle agent deletion
  const handleDelete = async (agent) => {
    try {
      const response = await agentsAPI.delete(agent.id);
      if (response.success) {
        antMessage.success('Agent berhasil dihapus');
        fetchAgents();
      }
    } catch (error) {
      antMessage.error('Gagal menghapus agent');
      console.error('Error deleting agent:', error);
    }
  };

  // Toggle agent active status
  const handleToggleActive = async (agent) => {
    try {
      const response = await agentsAPI.update(agent.id, { is_active: !agent.is_active });
      if (response.success) {
        antMessage.success('Agent status berhasil diupdate');
        fetchAgents();
      }
    } catch (error) {
      antMessage.error('Gagal mengupdate status agent');
      console.error('Error toggling agent:', error);
    }
  };

  // Copy agent credentials (.env format)
  const copyCredentials = (agent) => {
    const text = [
      `AGENT_ID=${agent.agent_id}`,
      `AGENT_TOKEN=${agent.token}`,
      `AGENT_SECRET=${agent.secret || ''}`,
    ].join('\n');
    navigator.clipboard.writeText(text).then(() => {
      antMessage.success('Kredensial disalin ke clipboard');
    }).catch(() => {
      antMessage.error('Gagal menyalin kredensial');
    });
  };

  // Regenerate token (also returns secret for .env)
  const handleRegenerateToken = async (agent) => {
    try {
      const response = await agentsAPI.regenerateToken(agent.id);
      if (response.success) {
        antMessage.success('Token berhasil diregenerasi — agent akan reconnect otomatis jika Agent Secret sudah benar');
        const { token, secret, agent_id } = response.data;
        setSelectedAgent((prev) => ({
          ...prev,
          agent_id: agent_id || prev?.agent_id,
          token,
          secret,
        }));
        fetchAgents();
      }
    } catch (error) {
      antMessage.error('Gagal meregenerasi token');
      console.error('Error regenerating token:', error);
    }
  };

  const handleRegenerateSecret = async (agent) => {
    try {
      const response = await agentsAPI.regenerateSecret(agent.id);
      if (response.success) {
        antMessage.warning('Secret baru — salin Agent Secret ke .env di mesin agent');
        setSelectedAgent((prev) => ({
          ...prev,
          secret: response.data.secret,
        }));
        fetchAgents();
      }
    } catch (error) {
      antMessage.error('Gagal meregenerasi secret');
      console.error('Error regenerating secret:', error);
    }
  };

  // Navigate to agent detail
  const viewAgentDetail = (agent) => {
    navigate(`/agents/${agent.agent_id}`);
  };

  // Get platform icon
  const getPlatformIcon = (platform) => {
    switch (platform) {
      case 'windows':
        return '🪟';
      case 'linux':
        return '🐧';
      case 'macos':
        return '🍎';
      default:
        return '💻';
    }
  };

  // Table columns
  const columns = [
    {
      title: 'Agent Name',
      dataIndex: 'agent_name',
      key: 'agent_name',
      render: (text, record) => (
        <Space>
          <span style={{ fontSize: '18px', marginRight: 8 }}>
            {getPlatformIcon(record.platform)}
          </span>
          <span>{text}</span>
          {!record.is_active && <Tag color="red" style={{ marginLeft: 8 }}>Inactive</Tag>}
        </Space>
      )
    },
    {
      title: 'Agent ID',
      dataIndex: 'agent_id',
      key: 'agent_id',
      render: (text) => <code style={{ fontSize: '12px' }}>{text}</code>
    },
    {
      title: 'Platform',
      dataIndex: 'platform',
      key: 'platform',
      render: (text) => <Tag color="blue">{text?.toUpperCase()}</Tag>
    },
    {
      title: 'Type',
      dataIndex: 'agent_type',
      key: 'agent_type',
      render: (text) => <Tag color="green">{text}</Tag>
    },
    {
      title: 'Status',
      dataIndex: 'online',
      key: 'online',
      render: (online) => (
        <Badge
          status={online ? 'success' : 'default'}
          text={online ? 'Online' : 'Offline'}
          color={online ? '#52c41a' : '#d9d9d9'}
        />
      )
    },
    {
      title: 'Last Seen',
      dataIndex: 'last_seen',
      key: 'last_seen',
      render: (lastSeen) => lastSeen ? new Date(lastSeen).toLocaleString('id-ID') : '-'
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="View Details">
            <Button
              type="text"
              icon={<TeamOutlined />}
              onClick={() => viewAgentDetail(record)}
            />
          </Tooltip>
          <Tooltip title="Edit">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => openEditModal(record)}
            />
          </Tooltip>
          <Tooltip title="View Credentials">
            <Button
              type="text"
              icon={<KeyOutlined />}
              onClick={() => openCredentialModal(record)}
            />
          </Tooltip>
          <Tooltip title="Toggle Active">
            <Button
              type="text"
              icon={record.is_active ? <StopOutlined /> : <CheckCircleOutlined />}
              onClick={() => handleToggleActive(record)}
            />
          </Tooltip>
          <Popconfirm
            title="Hapus agent ini?"
            description={`Anda yakin ingin menghapus agent "${record.agent_name}"?`}
            onConfirm={() => handleDelete(record)}
            okText="Ya"
            cancelText="Tidak"
          >
            <Tooltip title="Delete">
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      )
    }
  ];

  // Get stats
  const onlineAgents = agents.filter(a => a.online).length;
  const activeAgents = agents.filter(a => a.is_active).length;
  const totalAgents = agents.length;

  return (
    <div style={{ padding: 24 }}>
      <Card
        title={
          <Space style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
            <span><TeamOutlined /> Agents Management</span>
            <Space>
              <Input
                placeholder="Search agent..."
                prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
                value={agentSearch}
                onChange={(e) => setAgentSearch(e.target.value)}
                allowClear
                style={{ width: 220 }}
              />
              <Button
                icon={<ReloadOutlined />}
                onClick={fetchAgents}
                loading={loading}
              >
                Refresh
              </Button>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={openCreateModal}
              >
                Add Agent
              </Button>
            </Space>
          </Space>
        }
      >
        {/* Statistics */}
        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col xs={24} sm={12} md={6}>
            <Card size="small">
              <Statistic
                title="Total Agents"
                value={totalAgents}
                prefix={<TeamOutlined />}
                valueStyle={{ color: totalAgents > 0 ? '#1890ff' : '#d9d9d9' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card size="small">
              <Statistic
                title="Active Agents"
                value={activeAgents}
                prefix={<CheckCircleOutlined />}
                valueStyle={{ color: activeAgents > 0 ? '#52c41a' : '#d9d9d9' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card size="small">
              <Statistic
                title="Online Agents"
                value={onlineAgents}
                prefix={<Badge status="success" />}
                valueStyle={{ color: onlineAgents > 0 ? '#52c41a' : '#d9d9d9' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card size="small">
              <Statistic
                title="Offline Agents"
                value={totalAgents - onlineAgents}
                prefix={<Badge status="default" />}
                valueStyle={{ color: totalAgents - onlineAgents > 0 ? '#ff4d4f' : '#d9d9d9' }}
              />
            </Card>
          </Col>
        </Row>

        {/* Agents Table */}
        <Table
          columns={columns}
          dataSource={agents.filter(a => {
            if (!agentSearch) return true;
            const q = agentSearch.toLowerCase();
            return (
              (a.agent_name || '').toLowerCase().includes(q) ||
              (a.agent_id || '').toLowerCase().includes(q) ||
              (a.platform || '').toLowerCase().includes(q) ||
              (a.agent_type || '').toLowerCase().includes(q) ||
              (a.agent_description || '').toLowerCase().includes(q) ||
              (a.online ? 'online' : 'offline').includes(q)
            );
          })}
          rowKey="id"
          loading={loading}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} dari ${total} agents`
          }}
        />
      </Card>

      {/* Create/Edit Agent Modal */}
      <Modal
        title={modalMode === 'create' ? 'Create New Agent' : 'Edit Agent'}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{
            agent_type: 'generic',
            is_active: true
          }}
        >
          <Form.Item
            label="Agent Name"
            name="agent_name"
            rules={[{ required: true, message: 'Agent name is required' }]}
          >
            <Input placeholder="e.g., Production Server 01" />
          </Form.Item>

          <Form.Item
            label="Agent Description"
            name="agent_description"
            rules={[{ required: true, message: 'Agent description is required' }]}
          >
            <TextArea rows={3} placeholder="Description of this agent" />
          </Form.Item>

          <Form.Item
            label="Agent Type"
            name="agent_type"
            rules={[{ required: true, message: 'Agent type is required' }]}
          >
            <select style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #d9d9d9' }}>
              <option value="linux">Linux</option>
              <option value="windows">Windows</option>
              <option value="macos">macOS</option>
              <option value="generic">Generic</option>
            </select>
          </Form.Item>

          <Form.Item
            label="Active"
            name="is_active"
            valuePropName="checked"
          >
            <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => setModalVisible(false)}>Cancel</Button>
              <Button type="primary" htmlType="submit">
                {modalMode === 'create' ? 'Create Agent' : 'Update Agent'}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Credentials Modal */}
      <Modal
        title="Agent Credentials"
        open={credentialModalVisible}
        onCancel={() => setCredentialModalVisible(false)}
        footer={
          <Space wrap>
            <Button onClick={() => setCredentialModalVisible(false)}>Close</Button>
            <Button onClick={() => copyCredentials(selectedAgent)}>
              Copy
            </Button>
            <Button onClick={() => handleRegenerateSecret(selectedAgent)}>
              Regenerate Secret
            </Button>
            <Button
              type="primary"
              danger
              onClick={() => handleRegenerateToken(selectedAgent)}
            >
              Regenerate Token
            </Button>
          </Space>
        }
        width={600}
      >
        {selectedAgent && (
          <div>
            <Form layout="vertical">
              <Form.Item label="Agent ID">
                <Input
                  value={selectedAgent.agent_id}
                  readOnly
                  style={{ fontFamily: 'monospace', backgroundColor: '#f5f5f5' }}
                />
              </Form.Item>

              <Form.Item label="Agent Token">
                <Input.TextArea
                  value={selectedAgent.token}
                  readOnly
                  rows={4}
                  style={{ fontFamily: 'monospace', backgroundColor: '#f5f5f5' }}
                />
              </Form.Item>

              <Form.Item label="Agent Secret">
                <Input.TextArea
                  value={selectedAgent.secret || '(belum ada — klik Regenerate Secret)'}
                  readOnly
                  rows={2}
                  style={{ fontFamily: 'monospace', backgroundColor: '#f5f5f5' }}
                />
              </Form.Item>

              <div style={{ marginTop: 16, padding: 12, backgroundColor: '#fffbe6', borderRadius: 4, fontSize: 12 }}>
                <strong>Credential Guide</strong>
                <p style={{ margin: '8px 0 4px' }}>
                  <strong>First Installation</strong>
                </p>
                <ol style={{ marginTop: 4, paddingLeft: 16, marginBottom: 12 }}>
                  <li>Click the <strong>Copy</strong> button</li>
                  <li>Paste it into the <code></code> file on the agent server, then start the agent service</li>
                </ol>
                <p style={{ margin: '4px 0' }}>
                  <strong>Regenerate Token</strong>
                </p>
                <ol style={{ marginTop: 4, paddingLeft: 16, marginBottom: 12 }}>
                  <li>Agents already running with the correct <code>Agent Secret</code> will <strong>Reconnect Automatically</strong></li>
                  <li>Manually copy it to <code></code> only if the secret is incorrect or the agent has never connected before</li>
                </ol>
                <p style={{ margin: '4px 0' }}>
                  <strong>Regenerate Secret</strong>
                </p>
                <ol style={{ marginTop: 4, paddingLeft: 16, marginBottom: 0 }}>
                  <li>Copy the new <code>Agent Secret</code> to every agent machine</li>
                </ol>
              </div>
            </Form>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default Agents;
